import React, { useState } from 'react'
import { Alert, Button, Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { configService, DMSConfig } from '../../services/config.service'
import { settingsService } from '../../services/settings.service'
import { PermissionAction, PermissionType } from '../../services/permissions.service'
import { toastService } from '../../services/toast.service'
import { IfPermissions } from '../common/IfPermissions'
import { ESignatureCredentialsCard } from './Components/ESignature/ESignatureCredentialsCard'
import { ESignatureFinishSetupBlock } from './Components/ESignature/ESignatureFinishSetupBlock'

export interface SettingsESignatureSectionProps {
  configLoading: boolean
  config: DMSConfig | Partial<DMSConfig> | null
  configFormData: DMSConfig | Partial<DMSConfig>
  setConfigFormData: React.Dispatch<React.SetStateAction<DMSConfig | Partial<DMSConfig>>>
  handleConfigInputChange: (key: string, value: unknown) => void
  handleConfigSave: () => void
  handleConfigDiscard: () => void
  isConfigDirty: () => boolean
  isConfigFormValid: () => boolean
  configSaving: boolean
  isElectronicSignatureModuleEnabled: boolean
}

export const SettingsESignatureSection: React.FC<SettingsESignatureSectionProps> = ({
  configLoading,
  config,
  configFormData,
  setConfigFormData,
  handleConfigInputChange,
  handleConfigSave,
  handleConfigDiscard,
  isConfigDirty,
  isConfigFormValid,
  configSaving,
  isElectronicSignatureModuleEnabled,
}) => {
  const { t } = useTranslation()

  const [docusignConfigLoading, setDocusignConfigLoading] = useState(false)
  const [docusignFinishSetupLoading, setDocusignFinishSetupLoading] = useState(false)
  const [docusignFinishSetupMessage, setDocusignFinishSetupMessage] = useState<string | null>(null)
  const [docusignFinishSetupVariant, setDocusignFinishSetupVariant] = useState<'success' | 'danger' | 'warning'>('success')
  const [docusignFinishSetupConsentUrl, setDocusignFinishSetupConsentUrl] = useState<string | null>(null)
  const [docusignFinishSetupRedirectUri, setDocusignFinishSetupRedirectUri] = useState<string | null>(null)

  const eSignatureControlsDisabled = !isElectronicSignatureModuleEnabled || configSaving
  const hasWebhookRegistered = !!(configFormData.docusign_connect_webhook_info?.connect_id ?? configFormData.docusign_connect_webhook_info?.url)

  const handleRevealConfig = async () => {
    setDocusignConfigLoading(true)
    try {
      const revealed = await configService.getDocusignReveal()
      setConfigFormData((prev) => ({
        ...prev,
        docusign_integration_key: revealed.docusign_integration_key ?? null,
        docusign_secret_key: revealed.docusign_secret_key ?? null,
        docusign_user_id: revealed.docusign_user_id ?? null,
        docusign_account_id: revealed.docusign_account_id ?? null,
      }))
    } catch {
      toastService.showError(t('error_loading_config'))
    } finally {
      setDocusignConfigLoading(false)
    }
  }

  const handleRunFinishSetup = async () => {
    setDocusignFinishSetupMessage(null)
    setDocusignFinishSetupConsentUrl(null)
    setDocusignFinishSetupRedirectUri(null)
    setDocusignFinishSetupLoading(true)
    try {
      const override = (configFormData.docusign_connect_url_override ?? '').trim()
      const result = await configService.finishDocusignSetup({
        ...(override ? { connect_url: override } : {}),
      })
      if (result.success) {
        setDocusignFinishSetupVariant('success')
        setDocusignFinishSetupMessage(t('docusign.finishSetup.success'))
        setDocusignFinishSetupConsentUrl(null)
        setDocusignFinishSetupRedirectUri(null)
        setConfigFormData((prev) => ({
          ...prev,
          docusign_connect_webhook_info: {
            connect_id: result.connect_id,
            name: result.name,
            url: result.url,
            registered_at: result.registered_at,
          },
        }))
      } else {
        setDocusignFinishSetupVariant('danger')
        setDocusignFinishSetupMessage(result.error || result.api_body || 'Unknown error')
        setDocusignFinishSetupConsentUrl(result.consent_url ?? null)
        setDocusignFinishSetupRedirectUri(result.redirect_uri ?? null)
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { success?: boolean; error?: string; api_body?: string; consent_url?: string; redirect_uri?: string } } }
      const data = err?.response?.data
      if (data && data.success === false && data.error) {
        setDocusignFinishSetupVariant('danger')
        setDocusignFinishSetupMessage(data.error || data.api_body || 'Unknown error')
        setDocusignFinishSetupConsentUrl(data.consent_url ?? null)
        setDocusignFinishSetupRedirectUri(data.redirect_uri ?? null)
      } else {
        setDocusignFinishSetupVariant('danger')
        const msg = (e as Error)?.message ?? String(e)
        setDocusignFinishSetupMessage(msg || null)
        setDocusignFinishSetupConsentUrl(null)
        setDocusignFinishSetupRedirectUri(null)
      }
    } finally {
      setDocusignFinishSetupLoading(false)
    }
  }

  return (
    <IfPermissions action={PermissionAction.Change} type={PermissionType.AppConfig}>
      {configLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
        </div>
      ) : !config ? (
        <Alert variant="danger">{t('error_loading_config')}</Alert>
      ) : (
        <Form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault()
            if (isElectronicSignatureModuleEnabled && !configSaving) {
              handleConfigSave()
            }
          }}
        >
          <div className="settings-card">
            <div className="settings-card__body">
              {!isElectronicSignatureModuleEnabled && (
                <Alert variant="warning" className="mb-3">
                  {t('e_signature_module_disabled_message', {
                    defaultValue: 'Electronic signature is not available in your current package.',
                  })}
                </Alert>
              )}
              <Row className="g-2">
                <Col>
                  <ESignatureCredentialsCard
                    configFormData={configFormData}
                    disabled={eSignatureControlsDisabled}
                    configLoading={docusignConfigLoading}
                    onRevealConfig={handleRevealConfig}
                    onInputChange={handleConfigInputChange}
                  >
                    {!hasWebhookRegistered && (
                    <ESignatureFinishSetupBlock
                      configFormData={configFormData}
                      disabled={eSignatureControlsDisabled}
                      loading={docusignFinishSetupLoading}
                      message={docusignFinishSetupMessage}
                      messageVariant={docusignFinishSetupVariant}
                      consentUrl={docusignFinishSetupConsentUrl}
                      redirectUri={docusignFinishSetupRedirectUri}
                      showConnectUrlOverride={settingsService.getBoolean('show_docusign_connect_url_override', false)}
                      onConnectUrlOverrideChange={(v) => handleConfigInputChange('docusign_connect_url_override', v)}
                      onRunFinishSetup={handleRunFinishSetup}
                    />
                    )}
                  </ESignatureCredentialsCard>
                </Col>
              </Row>
            </div>
          </div>
          <div className="d-flex gap-2 mb-3 mt-3">
            <Button type="button" variant="outline-secondary" onClick={handleConfigDiscard} disabled={eSignatureControlsDisabled || !isConfigDirty()}>
              {t('discard')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!isElectronicSignatureModuleEnabled || configSaving || !isConfigFormValid() || !isConfigDirty()}
            >
              {configSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  {t('saving')}
                </>
              ) : (
                t('save')
              )}
            </Button>
          </div>
        </Form>
      )}
    </IfPermissions>
  )
}
